import { Link } from "react-router-dom";
import { ArrowRight, Camera, ClipboardList, FileText, Info, Ruler } from "lucide-react";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import CtaSection from "@/components/CtaSection";
import { IMAGES } from "@/data/site";

const INCLUDED = [
    { icon: ClipboardList, title: "Site Visit", text: "A scheduled visit to your site at the agreed scope and stage of work." },
    { icon: Camera, title: "Photographic Documentation", text: "Photographs of visible conditions, progress and workmanship, attached to the report." },
    { icon: ClipboardList, title: "Checklist-Based Observations", text: "Structured observations covering progress, materials and workmanship." },
    { icon: Ruler, title: "Basic Measurements", text: "Measurements taken where appropriate and accessible within the agreed scope." },
    { icon: ClipboardList, title: "Progress Observations", text: "An independent view of progress against the stage claimed by the contractor." },
    { icon: ClipboardList, title: "Workmanship Observations", text: "Visible workmanship reviewed against generally accepted good practice." },
    { icon: FileText, title: "Documentation of Visible Issues", text: "Issues observed on site recorded clearly, with priorities indicated." },
    { icon: FileText, title: "Recommendations", text: "Practical recommended actions for each significant observation." },
    { icon: FileText, title: "Digital Report", text: "A structured report with a unique Report ID, delivered for your records." },
];

export default function SiteInspection() {
    return (
        <div>
            <Seo
                title="Construction Site Inspection in Gorakhpur | The Sens Civil Works Consultancy"
                description="Independent construction site inspection in Gorakhpur — site visits, photographic documentation, checklist-based observations, measurements and a digital report."
            />
            <PageHero
                overline="Site Inspection & Technical Reporting"
                title="Independent Construction Site Inspection"
                description="Request an independent review of visible construction progress and workmanship — documented with photographs, observations and practical recommendations."
            />

            <section className="bg-white py-20 lg:py-28" data-testid="inspection-included">
                <div className="mx-auto max-w-7xl px-6 lg:px-12">
                    <Reveal>
                        <p className="font-mono text-xs uppercase tracking-[0.25em] text-amber">Scope</p>
                        <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-navy sm:text-4xl">
                            What an Inspection Includes
                        </h2>
                    </Reveal>
                    <div className="mt-12 grid grid-cols-1 gap-px border border-slate-200 bg-slate-200 sm:grid-cols-2 lg:grid-cols-3">
                        {INCLUDED.map((item, i) => (
                            <Reveal key={item.title} delay={Math.min(i * 0.05, 0.35)} className="bg-white">
                                <div className="h-full p-7">
                                    <item.icon size={22} strokeWidth={1.5} className="text-amber" />
                                    <h3 className="mt-4 font-display text-base font-bold text-navy">{item.title}</h3>
                                    <p className="mt-2 text-sm leading-relaxed text-slate-600">{item.text}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>

                    <Reveal delay={0.1}>
                        <div
                            className="mt-14 flex items-start gap-4 border border-amber/40 bg-amber/5 p-6"
                            data-testid="inspection-disclaimer"
                        >
                            <Info size={20} className="mt-0.5 shrink-0 text-amber" />
                            <p className="text-sm leading-relaxed text-slate-700">
                                <strong className="text-navy">Please note:</strong> The inspection is based on the
                                agreed scope and visible/site-accessible conditions and does not constitute a
                                structural safety certification unless specifically stated in the engagement.
                            </p>
                        </div>
                    </Reveal>

                    <Reveal delay={0.15}>
                        <Link
                            to="/contact?service=Site%20Inspection"
                            className="mt-10 inline-flex items-center gap-2 bg-navy px-7 py-4 font-mono text-xs font-semibold uppercase tracking-[0.18em] text-white transition-colors duration-300 hover:bg-amber"
                            data-testid="inspection-request-btn"
                        >
                            Request Site Inspection <ArrowRight size={14} />
                        </Link>
                    </Reveal>
                </div>
            </section>

            <section className="border-t border-slate-200 bg-background py-20 lg:py-28">
                <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-6 lg:grid-cols-12 lg:px-12">
                    <div className="lg:col-span-6">
                        <Reveal>
                            <div className="tick-frame">
                                <img
                                    src={IMAGES.inspection.url}
                                    alt={IMAGES.inspection.alt}
                                    className="aspect-[4/3] w-full object-cover"
                                    loading="lazy"
                                    data-testid="inspection-image"
                                />
                            </div>
                        </Reveal>
                    </div>
                    <div className="lg:col-span-6">
                        <Reveal>
                            <p className="font-mono text-xs uppercase tracking-[0.25em] text-amber">When to Call</p>
                            <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-navy sm:text-4xl">
                                The Right Moments for an Independent Inspection
                            </h2>
                            <ul className="mt-8 space-y-4">
                                {[
                                    "Before releasing a stage payment to the contractor",
                                    "At foundation, slab casting and brickwork stages",
                                    "Before accepting completed or renovated work",
                                    "When progress or quality on site feels uncertain",
                                    "Periodically, as an owner's independent site record",
                                ].map((item) => (
                                    <li key={item} className="flex items-start gap-3 border-b border-slate-200 pb-4 text-sm text-slate-700">
                                        <span className="mt-1.5 h-2 w-2 shrink-0 rotate-45 bg-amber" aria-hidden="true" />
                                        {item}
                                    </li>
                                ))}
                            </ul>
                        </Reveal>
                    </div>
                </div>
            </section>

            <CtaSection
                overline="Book an Inspection"
                title="Know What's Really Happening on Your Site"
                description="Share your site details and current stage of work — an inspection scope and fee will be agreed before the visit."
            />
        </div>
    );
}
